import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import * as routes from "../../routes";
import * as style from "../../../styles/style.css.classes";
import { period } from "../../domain/periods";
import {
  internalteacherid,
  thirdpartysellerid,
} from "../../../sql/classy/types";
import { Form, constantField, selectBox } from "trader.ts/ui.forms";
import { dateInDaterange } from "../../lib/time";
import { renderPeriod } from "../../translations_specific";
import {
  HHmm,
  renderFirstNameLastName,
  renderTimePlusWeekday,
} from "../../render";
import { LocalDate } from "@js-joda/core";
import {
  renderReportResultWithSorting,
  runQueryOnFormChange,
} from "../finance_invoices/client";
import { GetReturnTypeFromApiSpec } from "trader.ts/router";
import { checkAllCasesHandled } from "trader.ts/utils";
import { renderTableDownloadLinks } from "../../client/downloadtable";
import { renderTableHeaderCellWithSorting } from "../payments_overview_non_periodic/client";
import { parseWeekday } from "../../parsing_from_ur";

type possibleSorting = "student" | "teacher" | "instrument" | "time";

export function render(opts: {
  periods: period[];
  internalTeachers: {
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
  }[];
  thirdPartySellers: { id: thirdpartysellerid; name: string }[];
}): HTMLElement | HTMLElement[] {
  const today = LocalDate.now();

  const form = new Form({})
    .addField("period", [], async () =>
      selectBox({
        initial:
          opts.periods.find((p) =>
            dateInDaterange(
              { date: today },
              { from: p.firstday, until: p.lastday },
            ),
          ) || opts.periods[0],

        values: opts.periods,
        show: (p) => renderPeriod(p.firstday, p.lastday),
        label: "Schooljaar",
        // render: radioFieldCheckboxRender,
      }),
    )
    .addField("thirdpartyseller", [], async () => {
      const values = [
        { tag: "none" as const },
        ...opts.thirdPartySellers.map((s) => ({
          tag: "thirdpartyseller" as const,
          item: s,
        })),
      ];
      return opts.thirdPartySellers.length === 0
        ? constantField({ tag: "none" as const })
        : selectBox({
            initial: values[0],
            values: values,
            show: (n) => (n.tag === "none" ? "Geen" : n.item.name),
            // render: radioFieldCheckboxRender,
            label: "Vereniging",
          });
    })
    .addField("teacher_", [], async () => {
      const values = [
        {
          label: "Allemaal",
          values: opts.internalTeachers,
        },
        ...opts.internalTeachers.map((t) => ({
          label: renderFirstNameLastName(t),
          values: [t],
        })),
      ];
      return selectBox({
        values: values,
        initial: values[0],
        show: (t) => t.label,
        label: "Docenten",
      });
    })
    .addField("teachers", ["teacher_"], async ({ teacher_ }) =>
      constantField(teacher_.values),
    )

    .build();

  const res = runQueryOnFormChange(
    form.s,
    routes.queryEvaluationsMissingSpec,
    (t) => t,
  );

  const id = "classy-table-id";

  const sortingS: Source<{
    sorting: possibleSorting;
    direction: "asc" | "desc";
  }> = new Source({ sorting: "student", direction: "asc" });

  function doSorting<
    ROW extends GetReturnTypeFromApiSpec<
      typeof routes.queryEvaluationsMissingSpec
    >[number],
  >(s: possibleSorting): (r: ROW) => number | string {
    return (r: ROW) => {
      if (s === "student") {
        return renderFirstNameLastName(r.student);
      } else if (s === "teacher") {
        return renderFirstNameLastName(r.teacher);
      } else if (s === "time") {
        return r.lessongroup.time.format(HHmm);
      } else if (s === "instrument") {
        return r.instrument.description;
      } else {
        return checkAllCasesHandled(s);
      }
    };
  }

  return (
    <main style="max-width: unset;">
      <section className={style.equal_columns + style.cancel_main_margins}>
        {form.render()}
      </section>

      {renderTableDownloadLinks({ tableid: id, filename: "Evaluaties" })}

      {renderReportResultWithSorting(res, sortingS, (rows, _query, sorting) => {
        const renderTh = (label: string, s: possibleSorting | null) =>
          renderTableHeaderCellWithSorting(label, s, sorting, sortingS);
        const headerRow = (
          <tr>
            {renderTh("Student", "student")}
            {renderTh("Docent", "teacher")}
            {renderTh("Cursus", "instrument")}
            {renderTh("Tijdstip", "time")}
          </tr>
        );
        const renderedRows =
          rows === null
            ? [<tr></tr>]
            : rows
                .orderBy(doSorting(sorting.sorting), sorting.direction)
                .map((row) => {
                  return (
                    <tr>
                      <td>{renderFirstNameLastName(row.student)}</td>
                      <td>{renderFirstNameLastName(row.teacher)}</td>
                      <td>{row.instrument.description}</td>
                      <td>
                        <a
                          href={routes.lessongroup_generalRoute.link({
                            lessongroupid: row.lessongroup.id,
                          })}
                        >
                          {renderTimePlusWeekday(
                            row.lessongroup.time,
                            parseWeekday(row.lessongroup.weekday),
                          )}
                        </a>
                      </td>
                    </tr>
                  );
                });

        return (
          <table id={id}>
            {headerRow}
            {renderedRows}
          </table>
        );
      })}
    </main>
  );
}
